"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, cn, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Textarea } from "@ubud-ui/react-ui-kit";
import { buildFormSchema } from "@shared/lib/form";
import { TFormField } from "../../types";
import { useForm } from "react-hook-form";

export function DynamicForm({
  fields,
  defaultValues,
  children,
  onSubmit,
}: {
  fields: TFormField[];
  defaultValues: any;
  children: React.ReactNode;
  onSubmit: (payload: object) => void;
}) {
  const formSchema = buildFormSchema(fields);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  const getFormInput = (field: any, f: TFormField) => {
    if (f.inputType === "textarea") {
      return (
        <Textarea rows={3} className="!p-4 text-lg md:!text-xl" placeholder={f.placeholder} {...field} value={field.value as string} />
      );
    }

    if (f.inputType === "radio" && f.options && f.options.length > 0) {
      return (
        <div className="w-full rounded-full flex items-center bg-gray-200 overflow-hidden">
          {f.options?.map((option, index: number) => (
            <Button
              key={`form-field-${f.id}-option-${index}`}
              type="button"
              variant={"outline"}
              onClick={() => {
                form.resetField(f.name, {
                  defaultValue: option,
                });
              }}
              className={cn(
                "m-1 rounded-full text-md h-11 w-full font-semibold",
                field.value === option ? "border border-primary" : "border-none shadow-none bg-transparent",
              )}
            >
              <span className={cn(field.value == option ? "text-primary" : "text-gray-600")}>{option}</span>
            </Button>
          ))}
        </div>
      );
    }

    return (
      <Input
        className="!p-4 min-h-[50px] md:min-h-[60px] text-lg md:!text-xl"
        type={f.inputType}
        placeholder={f.placeholder}
        {...field}
        value={field.value as string}
      />
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {fields.map((f) => {
          const name = f.name;

          return (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  {f.label && <FormLabel className="text-base md:text-lg mb-2 inline-block">{f.label}</FormLabel>}
                  <FormControl>{getFormInput(field, f)}</FormControl>
                  <FormMessage className="text-sm md:text-base inline-block mt-2" />
                </FormItem>
              )}
            />
          );
        })}

        {children}
      </form>
    </Form>
  );
}
